import { useEffect, useState, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import { useParams, useNavigate } from "react-router-dom";
import { marked } from "marked";
import TurndownService from "turndown";
import { api, API_BASE, getToken } from "@/lib/api";
import { toast } from "sonner";
import { downloadPdfWithProgress } from "@/lib/pdfDownload";
import ConsultingBreadcrumbs from "@/components/ConsultingBreadcrumbs";
import { TableroBanner } from "@/components/tablero/TableroBanner";
import { renderRich } from "@/lib/renderRich";
import {
  ArrowLeft,
  Download,
  Loader2,
  AlertTriangle,
  RefreshCw,
  CheckCircle2,
  Copy,
  MessageCircle,
  Star,
  Clock,
  Sparkles,
  Target,
  TrendingUp,
  Share2,
  X,
  Mail,
  Link2,
  ShieldCheck,
  Send,
  HelpCircle,
  Gauge,
  Pencil,
  Check,
  Bold,
  Italic,
  List,
  ListOrdered,
  Link as LinkIcon,
  Undo2,
  Briefcase,
  ClipboardCheck,
  UserPlus,
  LineChart,
  MessageSquare,
  Grid3x3,
  ArrowRight,
} from "lucide-react";

// iter86 · turndown configurado para preservar la sintaxis markdown más
// cercana a la que produce el LLM (H2/H3 con `##/###`, listas, bold, links).
const _turndown = new TurndownService({
  headingStyle: "atx",
  bulletListMarker: "-",
  codeBlockStyle: "fenced",
  emDelimiter: "*",
});
// Preservar tablas markdown si aparecen (turndown por defecto las descarta).
_turndown.keep(["table", "thead", "tbody", "tr", "th", "td"]);

// Bloques editables (iter93·R2). El bloque 7 se edita con pipeline reforzado:
// el backend preserva automáticamente el marcador oculto `<!-- MADUREZ_JSON -->`
// si turndown lo descarta o el usuario lo borra sin querer.
const EDITABLE_BLOCKS = new Set([1, 2, 3, 4, 5, 6, 7]);

// iter95 · Mapa bloque → herramienta(s) que ayudan a aterrizar la
// recomendación. Fuente de verdad en backend (`consulting_tools.TOOL_META`);
// aquí lo duplicamos para evitar un roundtrip extra. Si cambia el catálogo,
// actualizar ambos. Diseño: 1 herramienta → banner grande (A); 2 herramientas
// → doble card compacta (B); bloque 7 → variante que apunta al dashboard.
const BLOCK_TOOLS = {
  1: [{ key: "job_offer", title: "Generador de ofertas de empleo", description: "Redacta una oferta con el EVP, cultura y tono de tu empresa en 90 segundos.", Icon: Briefcase }],
  2: [
    { key: "competency_profile", title: "Perfil de competencias", description: "Define técnicas, conductuales y ajuste cultural del puesto con niveles e indicadores.", Icon: Target },
    { key: "interview_assistant", title: "Asistente de entrevista", description: "Guía con preguntas por competencia, scoring 1-3-5 y resumen automático del candidato.", Icon: ClipboardCheck },
  ],
  3: [{ key: "onboarding_plan", title: "Plan de onboarding por incorporación", description: "Plan personalizado por empleado con acciones, responsables y check-ins a 30/60/90 días.", Icon: UserPlus }],
  4: [{ key: "evaluation", title: "Asistente de evaluación", description: "Convierte tu puntuación por criterios en un borrador de feedback para la conversación.", Icon: LineChart }],
  5: [{ key: "feedback", title: "Feedback estructurado", description: "Describe una situación en lenguaje libre y obtén feedback con el modelo de tu diagnóstico.", Icon: MessageSquare }],
  6: [{ key: "development_plan", title: "Plan de desarrollo individual", description: "Plan de crecimiento con objetivos, acciones, recursos, plazos y criterios de siguiente nivel.", Icon: TrendingUp }],
  // Bloque 7 (resumen) → CTA genérico que lleva al dashboard de herramientas.
  7: [{ key: "__dashboard__", title: "Explora las 7 herramientas", description: "Combina las herramientas de la Consultoría Premium para aterrizar cada recomendación.", Icon: Grid3x3 }],
};

// iter86 · Etiquetas cortas de una sola palabra para la barra móvil flotante.
// Se muestran bajo cada número.
const BLOCK_SHORT_LABELS = {
  1: "Marca",
  2: "Selección",
  3: "Onboarding",
  4: "Evaluación",
  5: "Feedback",
  6: "Desarrollo",
  7: "Cierre",
};

// iter86 · Botón de la toolbar del editor WYSIWYG per-bloque.
function ToolbarBtn({ onClick, title, children }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()} // preserva la selección del contenteditable
      onClick={onClick}
      title={title}
      className="p-1.5 rounded-md text-slate-500 hover:text-[#0F6E56] hover:bg-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0F6E56]/40"
    >
      {children}
    </button>
  );
}

const BLOCK_LABELS = {
  1: "Marca Empleadora",
  2: "Selección",
  3: "Onboarding",
  4: "Evaluación",
  5: "Feedback",
  6: "Desarrollo",
  7: "Cierre y Plan Global",
};

export default function ConsultingReport() {
  // iter95 · Banner CTA al final de cada bloque. Variantes:
  // - Variante "single" (bloques con 1 herramienta) → banner grande A.
  // - Variante "pair" (bloque 2, 2 herramientas) → doble card compacta B.
  // - Variante "dashboard" (bloque 7) → banner apuntando al grid de herramientas.
  function BlockToolCTA({ blockN, testId, navigate }) {
    const tools = BLOCK_TOOLS[blockN];
    if (!tools || !tools.length) return null;
    const isDashboard = tools[0]?.key === "__dashboard__";

    const go = (toolKey) => {
      if (!testId) return;
      if (toolKey === "__dashboard__") {
        navigate(`/consulting/${testId}/tools`);
      } else {
        navigate(`/consulting/${testId}/tools/${toolKey}`);
      }
    };

    // Variante B · doble card compacta.
    if (tools.length === 2) {
      return (
        <div
          className="mt-8 pt-6 border-t"
          style={{ borderColor: "rgba(11,61,46,0.08)" }}
          data-testid={`consulting-report-block-${blockN}-tools-cta`}
        >
          <p className="text-[11px] uppercase tracking-[0.18em] font-bold mb-3" style={{ color: "#0F6E56" }}>
            Herramientas de esta área
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {tools.map((t) => {
              const Icon = t.Icon;
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => go(t.key)}
                  className="group text-left rounded-xl p-4 border transition-all hover:border-[#0F6E56]/40 hover:shadow-md hover:-translate-y-0.5 bg-gradient-to-br from-white to-[#F6F3EC]/40"
                  style={{ borderColor: "rgba(11,61,46,0.12)" }}
                  data-testid={`consulting-report-block-${blockN}-tool-${t.key}`}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ background: "rgba(201,168,76,0.14)", color: "#8a6d2a" }}
                    >
                      <Icon className="w-4.5 h-4.5" strokeWidth={1.8} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <h4 className="font-display font-bold text-[14px] text-gray-900 leading-tight">
                          {t.title}
                        </h4>
                        <ArrowRight className="w-3.5 h-3.5 text-[#0F6E56] opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
                      </div>
                      <p className="mt-1 text-[12.5px] text-gray-600 leading-snug">
                        {t.description}
                      </p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      );
    }

    // Variantes A y "dashboard" (ambas single, estilo banner grande).
    const t = tools[0];
    const Icon = t.Icon;
    return (
      <div
        className="mt-8 pt-6 border-t"
        style={{ borderColor: "rgba(11,61,46,0.08)" }}
        data-testid={`consulting-report-block-${blockN}-tools-cta`}
      >
        <button
          type="button"
          onClick={() => go(t.key)}
          className="group block w-full text-left rounded-2xl p-5 sm:p-6 border transition-all hover:shadow-lg hover:-translate-y-0.5"
          style={{
            borderColor: "rgba(15,110,86,0.18)",
            background:
              "linear-gradient(135deg, rgba(15,110,86,0.06) 0%, rgba(201,168,76,0.08) 100%)",
          }}
          data-testid={`consulting-report-block-${blockN}-tool-${t.key}`}
        >
          <div className="flex items-start gap-4">
            <div
              className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm"
              style={{
                background: isDashboard
                  ? "linear-gradient(135deg, #0F6E56 0%, #0b3d2e 100%)"
                  : "linear-gradient(135deg, #C9A84C 0%, #8a6d2a 100%)",
                color: "white",
              }}
            >
              <Icon className="w-6 h-6 sm:w-7 sm:h-7" strokeWidth={1.75} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] sm:text-[11px] uppercase tracking-[0.2em] font-bold mb-1" style={{ color: "#0F6E56" }}>
                {isDashboard ? "Siguiente paso · Herramientas de gestión" : "Próximo paso · Herramienta recomendada"}
              </p>
              <h4 className="font-display font-bold text-lg sm:text-xl text-gray-900 leading-tight">
                {t.title}
              </h4>
              <p className="mt-1.5 text-[13.5px] sm:text-sm text-gray-600 leading-relaxed max-w-2xl">
                {t.description}
              </p>
              <div
                className="mt-3 inline-flex items-center gap-1.5 text-[13px] font-semibold group-hover:gap-2.5 transition-all"
                style={{ color: "#0F6E56" }}
              >
                {isDashboard ? "Abrir herramientas" : "Abrir herramienta"}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </div>
            </div>
          </div>
        </button>
      </div>
    );
  }

  const { testId } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [report, setReport] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [execSummary, setExecSummary] = useState(null);
  const [execLoading, setExecLoading] = useState(false);
  const [activeBlock, setActiveBlock] = useState(1);
  const [readProgress, setReadProgress] = useState(0);
  const [priorities, setPriorities] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(`consulting-priorities-${testId}`) || "{}");
    } catch (_e) {
      return {};
    }
  });
  const [shareOpen, setShareOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [shareEmails, setShareEmails] = useState("");
  const [shareMessage, setShareMessage] = useState("");
  const [shareSending, setShareSending] = useState(false);
  const [shareLoadingUrl, setShareLoadingUrl] = useState(false);
  const [tablero, setTablero] = useState(null);
  useEffect(() => {
    if (status?.status !== "ready") return;
    api.get(`/consulting/tests/${testId}/tablero/summary`).then((r) => setTablero(r.data)).catch(() => {});
  }, [status?.status, testId]);
  // iter86 · Edición WYSIWYG per-bloque (bloques 1-6, owner only)
  const [editingBlock, setEditingBlock] = useState(null); // n | null
  const [editingSaving, setEditingSaving] = useState(false);
  const editorRef = useRef(null);
  const editingInitialHtmlRef = useRef(""); // para "cancelar" y "deshacer completo"
  const pollRef = useRef(null);

  async function loadStatus() {
    try {
      const s = await api.get(`/consulting/tests/${testId}/report_status`);
      setStatus(s.data);
      if (s.data.status === "ready") {
        const r = await api.get(`/consulting/tests/${testId}/report`);
        setReport(r.data);
      } else if (s.data.status === "generating" || s.data.status === "submitted") {
        pollRef.current = setTimeout(loadStatus, 5000);
      }
    } catch (err) {
      toast.error("No pudimos cargar el informe");
    }
  }

  useEffect(() => {
    loadStatus();
    return () => {
      if (pollRef.current) clearTimeout(pollRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testId]);

  // Cargar resumen ejecutivo cuando el informe está ready.
  useEffect(() => {
    if (status?.status !== "ready" || !report) return;
    let cancelled = false;
    (async () => {
      setExecLoading(true);
      try {
        const r = await api.get(`/consulting/tests/${testId}/executive_summary`);
        if (!cancelled) setExecSummary(r.data);
      } catch (_err) {
        // silent: el resumen es un extra, no bloquea el informe.
      } finally {
        if (!cancelled) setExecLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status?.status, report]);

  // Reading progress bar (scroll global de la página).
  useEffect(() => {
    if (status?.status !== "ready") return;
    function onScroll() {
      const h = document.documentElement;
      const total = h.scrollHeight - h.clientHeight;
      if (total <= 0) return setReadProgress(0);
      const pct = Math.min(100, Math.max(0, (h.scrollTop / total) * 100));
      setReadProgress(pct);
    }
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [status?.status]);

  // Highlight del bloque activo con IntersectionObserver.
  useEffect(() => {
    if (status?.status !== "ready" || !report) return;
    const observers = [];
    const els = [1, 2, 3, 4, 5, 6, 7]
      .map((n) => document.getElementById(`block-${n}`))
      .filter(Boolean);
    const io = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible.length > 0) {
          const id = visible[0].target.id;
          const n = parseInt(id.replace("block-", ""), 10);
          if (n) setActiveBlock(n);
        }
      },
      { rootMargin: "-30% 0px -55% 0px", threshold: [0.05, 0.25, 0.5, 0.75, 1] }
    );
    els.forEach((el) => io.observe(el));
    observers.push(io);

    // Reveal fade-in-up al entrar en viewport (una sola vez por bloque)
    const revealIo = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.setAttribute("data-revealed", "true");
            revealIo.unobserve(entry.target);
          }
        });
      },
      { rootMargin: "0px 0px -8% 0px", threshold: 0 }
    );
    els.forEach((el) => revealIo.observe(el));
    observers.push(revealIo);
    return () => observers.forEach((o) => o.disconnect());
  }, [status?.status, report]);

  async function downloadPdf() {
    setDownloading(true);
    try {
      await downloadPdfWithProgress({
        url: `/consulting/tests/${testId}/pdf`,
        filename: `Consultoria_${(report?.company_name || "informe").replace(/\s+/g, "_")}.pdf`,
        kind: "consulting",
        label: "Preparando tu informe ejecutivo",
        variant: "premium",
        timeout: 300000,
      });
      // Toast premium con acción secundaria "enviar a tu equipo"
      toast.success("PDF descargado", {
        description: "¿Quieres enviarlo por email a tu equipo?",
        duration: 6000,
        action: {
          label: "Enviar por email",
          onClick: () => openShare(),
        },
      });
    } catch (err) {
      toast.error("No pudimos descargar el PDF");
    } finally {
      setDownloading(false);
    }
  }

  async function openShare() {
    setShareOpen(true);
    if (shareUrl) return; // ya cargado
    setShareLoadingUrl(true);
    try {
      const { data } = await api.post(`/consulting/tests/${testId}/share`);
      setShareUrl(data.public_url || "");
    } catch (err) {
      toast.error("No pudimos generar el link compartido");
    } finally {
      setShareLoadingUrl(false);
    }
  }

  function closeShare() {
    setShareOpen(false);
  }

  function copyShareUrl() {
    if (!shareUrl) return;
    navigator.clipboard.writeText(shareUrl).then(
      () => toast.success("Link copiado al portapapeles"),
      () => toast.error("No pudimos copiar")
    );
  }

  async function sendShareEmail() {
    const emails = shareEmails
      .split(/[,;\n\s]+/)
      .map((e) => e.trim())
      .filter(Boolean);
    if (emails.length === 0) {
      toast.error("Añade al menos un email");
      return;
    }
    if (emails.length > 5) {
      toast.error("Máximo 5 emails por envío");
      return;
    }
    setShareSending(true);
    try {
      const { data } = await api.post(`/consulting/tests/${testId}/share/email`, {
        emails,
        message: shareMessage || null,
      });
      const sent = (data.sent || []).length;
      const failed = (data.failed || []).length;
      if (sent > 0) toast.success(`Enviado a ${sent} destinatario${sent > 1 ? "s" : ""}`);
      if (failed > 0) toast.error(`Fallaron ${failed} envío${failed > 1 ? "s" : ""}`);
      if (sent > 0 && failed === 0) {
        setShareEmails("");
        setShareMessage("");
        setShareOpen(false);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No pudimos enviar los emails");
    } finally {
      setShareSending(false);
    }
  }

  async function retryGeneration() {
    setRetrying(true);
    try {
      await api.post(`/consulting/tests/${testId}/retry`);
      toast.success("Regenerando informe…");
      setStatus({ ...status, status: "generating" });
      pollRef.current = setTimeout(loadStatus, 3000);
    } catch (err) {
      toast.error("No pudimos reintentar");
    } finally {
      setRetrying(false);
    }
  }

  function copyBlock(n) {
    const md = report?.blocks?.[n];
    const text = typeof md === "string" ? md : md?.markdown || md?.text || "";
    if (!text) {
      toast.error("Bloque vacío");
      return;
    }
    navigator.clipboard.writeText(text).then(
      () => toast.success(`Bloque ${n} copiado`),
      () => toast.error("No pudimos copiar")
    );
  }

  function askAboutBlock(n) {
    // Deep-link a la lista de consultoría con hint de pregunta pre-cargada.
    const q = `Sobre el bloque ${n} · ${BLOCK_LABELS[n]}: `;
    navigate(`/consulting?ask=${testId}&q=${encodeURIComponent(q)}`);
  }

  function togglePriority(n) {
    setPriorities((prev) => {
      const next = { ...prev, [n]: !prev[n] };
      try {
        localStorage.setItem(`consulting-priorities-${testId}`, JSON.stringify(next));
      } catch (_e) {}
      const msg = next[n] ? `Bloque ${n} marcado como prioritario` : `Prioridad quitada del bloque ${n}`;
      toast.success(msg);
      return next;
    });
  }

  // iter86 · Edición WYSIWYG per-bloque ---------------------------------

  function startEditing(n) {
    if (!EDITABLE_BLOCKS.has(n)) {
      toast.info("Este bloque no es editable.");
      return;
    }
    const initialHtml = marked.parse(report?.blocks?.[n] || "", { async: false });
    editingInitialHtmlRef.current = initialHtml;
    setEditingBlock(n);
    // Después del render pintamos el HTML en el div contenteditable.
    setTimeout(() => {
      if (editorRef.current) {
        editorRef.current.innerHTML = initialHtml;
        editorRef.current.focus();
        // iter93·R2 · bloque 7: avisamos del marcador oculto para que el
        // usuario no se preocupe si no lo ve en el editor.
        if (n === 7) {
          toast.info(
            "Nota: el marcador interno de madurez se preserva automáticamente al guardar.",
            { duration: 6000 }
          );
        }
      }
    }, 50);
  }

  function cancelEditing() {
    setEditingBlock(null);
    editingInitialHtmlRef.current = "";
  }

  function applyFormat(command, value) {
    if (!editorRef.current) return;
    editorRef.current.focus();
    // document.execCommand sigue siendo la vía más simple para contenteditable
    // básico (deprecada pero soportada en todos los navegadores modernos).
    document.execCommand(command, false, value);
  }

  function resetToOriginalDraft() {
    if (!editorRef.current) return;
    editorRef.current.innerHTML = editingInitialHtmlRef.current;
    editorRef.current.focus();
  }

  async function saveEditing() {
    if (editingBlock == null || !editorRef.current) return;
    const html = editorRef.current.innerHTML;
    let markdown;
    try {
      markdown = _turndown.turndown(html).trim();
    } catch (e) {
      toast.error("No se pudo convertir el contenido a markdown. Revisa el bloque y vuelve a intentar.");
      return;
    }
    if (!markdown || markdown.length < 30) {
      toast.error("El contenido es demasiado corto (mínimo 30 caracteres).");
      return;
    }
    setEditingSaving(true);
    try {
      const res = await api.patch(
        `/consulting/tests/${testId}/report/blocks/${editingBlock}`,
        { content: markdown }
      );
      // Refresca el reporte local con el nuevo markdown.
      setReport((prev) => {
        if (!prev) return prev;
        const nextBlocks = { ...(prev.blocks || {}) };
        nextBlocks[String(editingBlock)] = res.data.content;
        const nextEdited = { ...(prev.blocks_edited || {}) };
        nextEdited[String(editingBlock)] = res.data.edited_meta;
        return { ...prev, blocks: nextBlocks, blocks_edited: nextEdited };
      });
      toast.success(
        res.data.was_first_edit
          ? `Bloque ${editingBlock} guardado. La versión original queda archivada por si quieres restaurarla más adelante.`
          : `Bloque ${editingBlock} actualizado.`
      );
      setEditingBlock(null);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Error al guardar";
      toast.error(detail);
    } finally {
      setEditingSaving(false);
    }
  }

  // Estado: `n` del bloque que se está restaurando (null si ninguno).
  const [restoringBlock, setRestoringBlock] = useState(null);

  // Restaurar la versión original (texto generado por Claude). Descarta
  // TODAS las ediciones del bloque. Backend: POST /tests/{id}/report/blocks/{n}/restore
  async function restoreBlock(n) {
    if (restoringBlock != null) return;
    const confirmed = window.confirm(
      `¿Seguro que quieres descartar tus ediciones del bloque ${n} y volver al texto original generado por Claude?\n\nEsta acción no se puede deshacer.`
    );
    if (!confirmed) return;
    setRestoringBlock(n);
    try {
      const res = await api.post(
        `/consulting/tests/${testId}/report/blocks/${n}/restore`,
      );
      // Refresca el reporte local: contenido original + quita la marca de editado.
      setReport((prev) => {
        if (!prev) return prev;
        const nextBlocks = { ...(prev.blocks || {}) };
        nextBlocks[String(n)] = res.data.content;
        const nextEdited = { ...(prev.blocks_edited || {}) };
        delete nextEdited[String(n)];
        return { ...prev, blocks: nextBlocks, blocks_edited: nextEdited };
      });
      // Si estaba editando ese mismo bloque, salir del modo edición.
      if (editingBlock === n) {
        setEditingBlock(null);
        editingInitialHtmlRef.current = "";
      }
      toast.success(`Bloque ${n} restaurado al texto original.`);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Error al restaurar";
      toast.error(detail);
    } finally {
      setRestoringBlock(null);
    }
  }

  const readingMinutes = useMemo(() => {
    if (execSummary?.reading_minutes) return execSummary.reading_minutes;
    // fallback: estimar por longitud de texto (200 palabras/min).
    if (!report?.blocks) return 12;
    let words = 0;
    Object.values(report.blocks).forEach((b) => {
      const t = typeof b === "string" ? b : b?.markdown || b?.text || "";
      words += (t.match(/\S+/g) || []).length;
    });
    return Math.max(8, Math.min(18, Math.round(words / 200)));
  }, [execSummary, report]);

  const generatedLabel = useMemo(() => {
    const iso = report?.finished_at || report?.started_at;
    if (!iso) return "";
    try {
      const d = new Date(iso);
      return d.toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" });
    } catch (_e) {
      return "";
    }
  }, [report]);

  if (!status) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 text-slate-500" data-testid="consulting-report-loading">
        <Loader2 className="animate-spin mr-2" /> Cargando…
      </div>
    );
  }

  const done = (status.blocks_done || []).length;

  return (
    <div
      className="min-h-screen py-8 md:py-12 px-4 md:px-6 overflow-x-hidden"
      style={{
        background:
          "radial-gradient(1200px 500px at 100% -10%, rgba(15,110,86,0.06) 0%, rgba(15,110,86,0) 60%)," +
          "radial-gradient(900px 400px at -10% 20%, rgba(201,168,76,0.06) 0%, rgba(201,168,76,0) 55%)," +
          "linear-gradient(180deg, #fafaf7 0%, #f5f5f1 100%)",
      }}
      data-testid="consulting-report"
    >
      {/* Reading progress bar (solo en estado ready) */}
      {status.status === "ready" && (
        <div
          className="fixed top-0 left-0 right-0 z-40 h-1 pointer-events-none"
          data-testid="consulting-report-progress-bar"
        >
          <div
            className="h-full transition-[width] duration-150"
            style={{
              width: `${readProgress}%`,
              background: "linear-gradient(90deg, #0b3d2e 0%, #0F6E56 50%, #C9A84C 100%)",
              boxShadow: "0 1px 8px rgba(201,168,76,0.5)",
            }}
          />
        </div>
      )}

      <div className="max-w-6xl mx-auto">
        <ConsultingBreadcrumbs
          crumbs={[
            { label: "Consultorías", to: "/consulting" },
            { label: report?.nickname || report?.company_name || "Consultoría", to: `/consulting/${testId}/report` },
            { label: "Informe" },
          ]}
        />
        <button
          onClick={() => navigate("/consulting")}
          className="text-xs font-semibold text-slate-500 hover:text-[#0F6E56] inline-flex items-center gap-1.5 mb-6 md:mb-8 transition-colors group"
          data-testid="consulting-report-back"
        >
          <ArrowLeft className="w-3.5 h-3.5 transition-transform group-hover:-translate-x-0.5" /> Volver a mis consultorías
        </button>

        {/* Estado GENERATING */}
        {(status.status === "generating" || status.status === "submitted") && (
          <div
            className="relative rounded-3xl p-8 md:p-12 text-center overflow-hidden max-w-4xl mx-auto"
            style={{
              background: "linear-gradient(180deg, #ffffff 0%, #fafaf7 100%)",
              boxShadow: "0 20px 60px -30px rgba(11,61,46,0.35), 0 0 0 1px rgba(11,61,46,0.08)",
            }}
          >
            <span
              aria-hidden
              className="absolute top-0 left-0 right-0 h-1"
              style={{ background: "linear-gradient(90deg, #0b3d2e 0%, #0F6E56 45%, #C9A84C 100%)" }}
            />
            <Loader2 className="w-12 h-12 mx-auto mb-4 animate-spin" style={{ color: "#0F6E56" }} />
            <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 mb-2">Generando tu informe</h1>
            <p className="text-slate-600 mb-6 text-sm md:text-base">
              Estamos procesando tus respuestas y redactando el análisis personalizado.
            </p>
            <div className="mb-4">
              <div className="text-4xl md:text-5xl font-bold tabular-nums" style={{ color: "#0F6E56" }} data-testid="consulting-report-progress">
                {done} <span className="text-slate-400">/ 7</span>
              </div>
              <div className="text-xs uppercase tracking-widest text-slate-500 mt-2">bloques completados</div>
            </div>
            <div className="w-full max-w-md mx-auto bg-slate-100 rounded-full h-3 overflow-hidden mb-6">
              <div
                className="h-full transition-all duration-700"
                style={{ width: `${(done / 7) * 100}%`, background: "linear-gradient(90deg, #0b3d2e 0%, #0F6E56 60%, #C9A84C 100%)" }}
              />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 max-w-lg mx-auto text-xs">
              {Object.entries(BLOCK_LABELS).map(([n, label]) => {
                const isDone = (status.blocks_done || []).includes(parseInt(n));
                return (
                  <div
                    key={n}
                    className="rounded-lg px-2 py-2 border flex items-center gap-1.5 transition-all"
                    style={{
                      background: isDone ? "#ecfdf5" : "#f8fafc",
                      borderColor: isDone ? "#0F6E56" : "#e2e8f0",
                      color: isDone ? "#0F6E56" : "#64748b",
                    }}
                  >
                    {isDone ? <CheckCircle2 className="w-3 h-3" /> : <Loader2 className="w-3 h-3 animate-spin" />}
                    <span className="font-medium truncate">{label}</span>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-slate-400 mt-6">
              Puedes cerrar esta pestaña. Cuando el informe esté listo recibirás un email.
            </p>
          </div>
        )}

        {/* Estado FAILED */}
        {status.status === "generating_failed" && (
          <div
            className="relative rounded-3xl p-8 md:p-12 text-center overflow-hidden max-w-4xl mx-auto"
            style={{
              background: "linear-gradient(180deg, #ffffff 0%, #fef7f7 100%)",
              boxShadow: "0 20px 60px -30px rgba(159,18,57,0.25), 0 0 0 1px rgba(244,63,94,0.15)",
            }}
          >
            <span
              aria-hidden
              className="absolute top-0 left-0 right-0 h-1"
              style={{ background: "linear-gradient(90deg, #9f1239 0%, #f43f5e 100%)" }}
            />
            <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-rose-500" />
            <h1 className="font-display text-2xl font-bold text-slate-900 mb-2">Algo falló al generar el informe</h1>
            <p className="text-slate-600 text-sm mb-2">{status.error || "Error desconocido"}</p>
            <p className="text-xs text-slate-400 mb-6">
              Los bloques ya generados están guardados; al reintentar solo se regenerarán los que faltan.
            </p>
            <button
              onClick={retryGeneration}
              disabled={retrying}
              className="rounded-full px-6 py-2.5 font-semibold text-white inline-flex items-center gap-2 disabled:opacity-50 transition-all hover:-translate-y-0.5"
              style={{
                background: "linear-gradient(135deg, #0b3d2e 0%, #0F6E56 100%)",
                boxShadow: "0 8px 22px -8px rgba(11,61,46,0.5)",
              }}
              data-testid="consulting-report-retry"
            >
              <RefreshCw className={`w-4 h-4 ${retrying ? "animate-spin" : ""}`} /> Reintentar generación
            </button>
          </div>
        )}

        {/* Skeleton: status=ready pero aún cargando /report o exec summary */}
        {status.status === "ready" && !report && (
          <div className="max-w-6xl mx-auto space-y-6 md:space-y-8" data-testid="consulting-report-skeleton">
            {/* Skeleton hero */}
            <div
              className="rounded-3xl p-6 md:p-8 relative overflow-hidden"
              style={{
                background: "linear-gradient(135deg, #0b3d2e 0%, #14523f 55%, #0F6E56 100%)",
              }}
            >
              <div className="skeleton-pulse h-4 w-40 rounded mb-3" style={{ background: "rgba(201,168,76,0.15)" }} />
              <div className="skeleton-pulse h-9 w-64 rounded mb-4" style={{ background: "rgba(255,255,255,0.12)" }} />
              <div className="skeleton-pulse h-3 w-56 rounded" style={{ background: "rgba(255,255,255,0.08)" }} />
            </div>
            {/* Skeleton exec summary */}
            <div className="rounded-3xl p-6 md:p-8 bg-white shadow-sm">
              <div className="skeleton-pulse h-3 w-48 rounded mb-6" />
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <div className="skeleton-pulse h-3 w-24 rounded mb-3" />
                  <div className="skeleton-pulse h-3 w-full rounded" />
                  <div className="skeleton-pulse h-3 w-11/12 rounded" />
                  <div className="skeleton-pulse h-3 w-10/12 rounded" />
                </div>
                <div className="space-y-2">
                  <div className="skeleton-pulse h-3 w-24 rounded mb-3" />
                  <div className="skeleton-pulse h-3 w-full rounded" />
                  <div className="skeleton-pulse h-3 w-11/12 rounded" />
                  <div className="skeleton-pulse h-3 w-10/12 rounded" />
                </div>
              </div>
            </div>
            {/* Skeleton bloques */}
            {[1, 2, 3].map((n) => (
              <div key={n} className="rounded-3xl p-6 md:p-8 bg-white shadow-sm">
                <div className="skeleton-pulse h-3 w-56 rounded mb-4" />
                <div className="skeleton-pulse h-6 w-3/4 rounded mb-4" />
                <div className="skeleton-pulse h-3 w-full rounded mb-2" />
                <div className="skeleton-pulse h-3 w-11/12 rounded mb-2" />
                <div className="skeleton-pulse h-3 w-4/5 rounded" />
              </div>
            ))}
          </div>
        )}

        {/* Estado READY */}
        {status.status === "ready" && report && (
          <>
            {/* Hero premium */}
            <div
              className="relative rounded-3xl p-6 md:p-8 mb-6 md:mb-8 overflow-hidden"
              style={{
                background: "linear-gradient(135deg, #0b3d2e 0%, #14523f 55%, #0F6E56 100%)",
                boxShadow: "0 30px 60px -30px rgba(11,61,46,0.55), 0 0 0 1px rgba(201,168,76,0.15) inset",
              }}
            >
              <span
                aria-hidden
                className="absolute -top-8 -right-8 pointer-events-none"
                style={{
                  width: 220,
                  height: 220,
                  background:
                    "radial-gradient(circle at 100% 0%, rgba(201,168,76,0.35) 0%, rgba(201,168,76,0) 65%)," +
                    "repeating-linear-gradient(135deg, rgba(255,255,255,0.05) 0, rgba(255,255,255,0.05) 1px, transparent 1px, transparent 10px)",
                }}
              />
              <span
                aria-hidden
                className="absolute bottom-0 left-0 right-0 h-1"
                style={{ background: "linear-gradient(90deg, #C9A84C 0%, #E5C874 50%, #C9A84C 100%)" }}
              />
              <div className="relative flex items-start justify-between gap-4 flex-col lg:flex-row">
                <div className="min-w-0 w-full lg:w-auto">
                  <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] font-semibold mb-3 px-3 py-1 rounded-full" style={{ background: "rgba(201,168,76,0.15)", color: "#E5C874" }}>
                    <span style={{ width: 6, height: 6, borderRadius: 999, background: "#C9A84C", display: "inline-block" }} />
                    Informe de Consultoría
                  </div>
                  <h1 className="font-display text-3xl md:text-4xl font-bold text-white leading-tight" style={{ letterSpacing: "-0.02em" }}>
                    {report.company_name}
                  </h1>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-3 text-xs md:text-sm" style={{ color: "rgba(255,255,255,0.75)" }}>
                    <span className="inline-flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5" style={{ color: "#E5C874" }} /> ~{readingMinutes} min lectura
                    </span>
                    <span className="hidden sm:inline text-white/30">·</span>
                    <span className="inline-flex items-center gap-1.5">
                      <Sparkles className="w-3.5 h-3.5" style={{ color: "#E5C874" }} /> 7 áreas · Análisis en profundidad
                    </span>
                  </div>

                  {/* Índice de madurez · pill sutil con tooltip explicativo */}
                  {report?.madurez_pct !== undefined && report.madurez_pct > 0 && (
                    <MadurezPill
                      pct={report.madurez_pct}
                      label={report.madurez_label}
                      map={report.madurez_map}
                    />
                  )}
                  {/* Timestamp + firma */}
                  {generatedLabel && (
                    <div className="mt-4 pt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]" style={{ borderTop: "1px solid rgba(255,255,255,0.12)", color: "rgba(255,255,255,0.55)" }}>
                      <span className="inline-flex items-center gap-1">
                        <ShieldCheck className="w-3 h-3" style={{ color: "#C9A84C" }} />
                        Generado el {generatedLabel}
                      </span>
                      <span className="text-white/25">·</span>
                      <span>Consultoría OptimizaRRHH</span>
                      <span className="text-white/25">·</span>
                      <span className="uppercase tracking-widest" style={{ color: "rgba(201,168,76,0.7)", fontWeight: 600 }}>Confidencial</span>
                    </div>
                  )}
                </div>
                <div className="flex gap-2 flex-wrap items-center w-full lg:w-auto lg:mt-40">
                  <button
                    onClick={() => navigate(`/consulting/${testId}/tools`)}
                    className="rounded-full px-3 md:px-5 py-2 md:py-2.5 text-xs md:text-sm font-semibold inline-flex items-center gap-2 transition-all hover:-translate-y-0.5"
                    style={{
                      background: "rgba(255,255,255,0.08)",
                      color: "#ffffff",
                      backdropFilter: "blur(8px)",
                      border: "1px solid rgba(201,168,76,0.35)",
                    }}
                    data-testid="consulting-report-open-tools"
                  >
                    Herramientas de gestión
                  </button>
                  <button
                    onClick={() => navigate(`/consulting/${testId}/checklist`)}
                    className="rounded-full px-3 md:px-5 py-2 md:py-2.5 text-xs md:text-sm font-semibold inline-flex items-center gap-2 transition-all hover:-translate-y-0.5"
                    style={{
                      background: "rgba(255,255,255,0.08)",
                      color: "#ffffff",
                      backdropFilter: "blur(8px)",
                      border: "1px solid rgba(201,168,76,0.35)",
                    }}
                    data-testid="consulting-report-open-checklist"
                  >
                    Checklist
                  </button>
                  <button
                    onClick={() => navigate(`/consulting/${testId}/perception`)}
                    className="rounded-full px-3 md:px-5 py-2 md:py-2.5 text-xs md:text-sm font-semibold inline-flex items-center gap-2 transition-all hover:-translate-y-0.5"
                    style={{
                      background: "rgba(255,255,255,0.08)",
                      color: "#ffffff",
                      backdropFilter: "blur(8px)",
                      border: "1px solid rgba(201,168,76,0.35)",
                    }}
                    data-testid="consulting-report-open-perception"
                    title="Contraste de percepciones con tu equipo"
                  >
                    Percepciones del equipo
                  </button>
                  {tablero?.available && tablero.dismissed && (
                    <button
                      onClick={() => navigate(`/consulting/${testId}/tablero`)}
                      className="rounded-full px-3 py-1.5 text-xs font-semibold underline underline-offset-2"
                      style={{ color: "#E5C874" }}
                      data-testid="consulting-report-tablero-link"
                    >
                      Tablero de talento
                    </button>
                  )}
                  <button
                    onClick={openShare}
                    className="rounded-full px-3 md:px-4 py-2 md:py-2.5 text-xs md:text-sm font-semibold inline-flex items-center gap-2 transition-all hover:-translate-y-0.5"
                    style={{
                      background: "linear-gradient(135deg, #34D399 0%, #10B981 100%)",
                      color: "#052e24",
                      border: "1px solid rgba(52,211,153,0.6)",
                      boxShadow: "0 8px 22px -8px rgba(52,211,153,0.45)",
                    }}
                    data-testid="consulting-report-share-btn"
                    title="Compartir este informe"
                  >
                    <Share2 className="w-4 h-4" strokeWidth={2.5} />
                    <span className="hidden sm:inline">Compartir</span>
                  </button>
                  {/* line-break: fuerza a Descargar PDF a caer en su propia fila,
                      justo debajo de "Percepciones del equipo" / "Compartir". */}
                  <div className="basis-full h-0" aria-hidden="true" />
                  <button
                    onClick={downloadPdf}
                    disabled={downloading}
                    className="group relative rounded-full px-4 md:px-6 py-2 md:py-2.5 text-xs md:text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50 transition-all hover:-translate-y-0.5 overflow-hidden mr-auto"
                    style={{
                      background: "linear-gradient(135deg, #C9A84C 0%, #E5C874 55%, #C9A84C 100%)",
                      color: "#0b3d2e",
                      boxShadow: "0 8px 22px -6px rgba(201,168,76,0.55)",
                    }}
                    data-testid="consulting-report-download-pdf"
                  >
                    {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" strokeWidth={2.5} />}
                    <span>{downloading ? "Generando…" : "Descargar PDF"}</span>
                  </button>
                </div>
              </div>
            </div>

            {tablero?.available && !tablero.dismissed && (
              <TableroBanner testId={testId} summary={tablero} onDismiss={() => setTablero((t) => ({ ...t, dismissed: true }))} />
            )}

            {/* Resumen ejecutivo */}
            {(execSummary || execLoading) && (
              <div
                className="rounded-3xl p-6 md:p-8 mb-6 md:mb-8 relative overflow-hidden"
                style={{
                  background: "linear-gradient(180deg, #ffffff 0%, #fdfcf7 100%)",
                  boxShadow: "0 10px 30px -18px rgba(11,61,46,0.18), 0 0 0 1px rgba(201,168,76,0.25)",
                }}
                data-testid="consulting-report-executive-summary"
              >
                <span
                  aria-hidden
                  className="absolute top-0 left-0 h-full w-1"
                  style={{ background: "linear-gradient(180deg, #C9A84C 0%, #0F6E56 100%)" }}
                />
                <div className="flex items-center gap-3 mb-5">
                  <span style={{ width: 24, height: 1.5, background: "#C9A84C", display: "inline-block" }} />
                  <div className="text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: "#0b3d2e" }}>
                    Resumen ejecutivo · 30 segundos
                  </div>
                </div>
                {execLoading && !execSummary ? (
                  <div className="flex items-center gap-2 text-slate-500 text-sm">
                    <Loader2 className="w-4 h-4 animate-spin" /> Preparando resumen ejecutivo…
                  </div>
                ) : execSummary ? (
                  <>
                    <div className="grid md:grid-cols-2 gap-6 mb-6">
                      <div>
                        <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-bold mb-3" style={{ color: "#0F6E56" }}>
                          <TrendingUp className="w-3.5 h-3.5" /> Fortalezas
                        </div>
                        <ul className="space-y-2">
                          {(execSummary.strengths || []).map((s, i) => (
                            <li key={i} className="flex gap-2.5 text-sm text-slate-700">
                              <span
                                className="mt-1 flex-shrink-0 w-1.5 h-1.5 rounded-full"
                                style={{ background: "#0F6E56" }}
                              />
                              <span>{renderRich(s)}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-bold mb-3" style={{ color: "#b45309" }}>
                          <Target className="w-3.5 h-3.5" /> Prioridades
                        </div>
                        <ul className="space-y-2">
                          {(execSummary.priorities || []).map((s, i) => (
                            <li key={i} className="flex gap-2.5 text-sm text-slate-700">
                              <span
                                className="mt-1 flex-shrink-0 w-1.5 h-1.5 rounded-full"
                                style={{ background: "#C9A84C" }}
                              />
                              <span>{renderRich(s)}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                    {execSummary.next_step && (
                      <div
                        className="rounded-2xl p-4 md:p-5"
                        style={{
                          background: "linear-gradient(135deg, rgba(15,110,86,0.06) 0%, rgba(201,168,76,0.08) 100%)",
                          border: "1px solid rgba(15,110,86,0.15)",
                        }}
                      >
                        <div className="text-[10px] uppercase tracking-[0.28em] font-bold mb-1.5" style={{ color: "#0b3d2e" }}>
                          Acción de mayor impacto esta semana
                        </div>
                        <p className="text-sm md:text-base font-medium text-slate-900 leading-snug">
                          {renderRich(execSummary.next_step)}
                        </p>
                      </div>
                    )}
                  </>
                ) : null}
              </div>
            )}

            {/* Layout desktop: sidebar sticky + contenido; móvil: índice arriba + contenido */}
            <div className="grid lg:grid-cols-[240px_1fr] gap-6 md:gap-8">
              {/* TOC Sticky (desktop lg+) */}
              <aside className="hidden lg:block">
                <nav
                  className="sticky top-8 rounded-3xl p-5"
                  style={{
                    background: "#ffffff",
                    boxShadow: "0 10px 30px -18px rgba(11,61,46,0.18), 0 0 0 1px rgba(11,61,46,0.06)",
                  }}
                  data-testid="consulting-report-toc"
                  aria-label="Índice del informe"
                >
                  <div className="flex items-center gap-2 mb-4">
                    <span style={{ width: 18, height: 1.5, background: "#C9A84C", display: "inline-block" }} />
                    <div className="text-[9px] uppercase tracking-[0.24em] font-bold" style={{ color: "#0b3d2e" }}>
                      Índice
                    </div>
                  </div>
                  <ul className="space-y-0.5">
                    {Object.entries(BLOCK_LABELS).map(([n, label]) => {
                      const num = parseInt(n, 10);
                      const isActive = activeBlock === num;
                      const isPriority = !!priorities[num];
                      return (
                        <li key={n}>
                          <a
                            href={`#block-${n}`}
                            className="group flex items-center gap-2.5 rounded-xl px-2.5 py-2 transition-all"
                            style={{
                              background: isActive ? "linear-gradient(90deg, rgba(15,110,86,0.08) 0%, rgba(201,168,76,0.08) 100%)" : "transparent",
                              borderLeft: isActive ? "2px solid #0F6E56" : "2px solid transparent",
                              paddingLeft: isActive ? "10px" : "10px",
                            }}
                            data-testid={`consulting-report-toc-item-${n}`}
                            aria-current={isActive ? "true" : undefined}
                          >
                            <span
                              className="w-6 h-6 rounded-full text-[10px] font-bold flex items-center justify-center flex-shrink-0 transition-transform"
                              style={{
                                background: isActive
                                  ? "linear-gradient(135deg, #0b3d2e 0%, #0F6E56 100%)"
                                  : "linear-gradient(135deg, #C9A84C 0%, #E5C874 100%)",
                                color: isActive ? "#E5C874" : "#0b3d2e",
                                boxShadow: isActive ? "0 2px 8px -2px rgba(11,61,46,0.5)" : "0 1px 3px -1px rgba(201,168,76,0.5)",
                              }}
                            >
                              {n}
                            </span>
                            <span
                              className="text-[13px] font-medium truncate flex-1"
                              style={{ color: isActive ? "#0b3d2e" : "#475569" }}
                            >
                              {label}
                            </span>
                            {isPriority && <Star className="w-3 h-3 flex-shrink-0" fill="#C9A84C" stroke="#C9A84C" />}
                          </a>
                        </li>
                      );
                    })}
                  </ul>
                </nav>
              </aside>

              {/* Contenido */}
              <div className="min-w-0">
                {/* Índice horizontal solo en móvil/tablet */}
                <div
                  className="lg:hidden rounded-3xl p-5 md:p-6 mb-6"
                  style={{
                    background: "#ffffff",
                    boxShadow: "0 10px 30px -18px rgba(11,61,46,0.18), 0 0 0 1px rgba(11,61,46,0.06)",
                  }}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <span style={{ width: 24, height: 1.5, background: "#C9A84C", display: "inline-block" }} />
                    <div className="text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: "#0b3d2e" }}>
                      Índice del informe
                    </div>
                  </div>
                  {/* iter89 · UI · Agrupación por fase (opción 5):
                      bloques 1-6 son el diagnóstico por área (2 columnas en sm+);
                      bloque 7 es la síntesis estratégica, destacada en cápsula
                      dorada aparte para dar jerarquía visual. */}
                  <div className="text-[10px] uppercase tracking-[0.24em] font-semibold text-slate-500 mb-2">
                    Diagnóstico por área
                  </div>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-5">
                    {Object.entries(BLOCK_LABELS)
                      .filter(([n]) => n !== "7")
                      .map(([n, label]) => (
                        <li key={n}>
                          <a
                            href={`#block-${n}`}
                            className="flex items-center gap-3 rounded-xl px-2 py-2 -mx-2 transition-colors hover:bg-slate-50"
                            data-testid={`consulting-nav-${n}`}
                          >
                            <span
                              className="w-7 h-7 rounded-full text-[11px] font-bold flex items-center justify-center flex-shrink-0"
                              style={{
                                background: "linear-gradient(135deg, #C9A84C 0%, #E5C874 100%)",
                                color: "#0b3d2e",
                                boxShadow: "0 2px 6px -1px rgba(201,168,76,0.5)",
                              }}
                            >
                              {n}
                            </span>
                            <span className="text-sm font-medium text-slate-700 truncate">{label}</span>
                            {priorities[parseInt(n, 10)] && <Star className="w-3.5 h-3.5 flex-shrink-0" fill="#C9A84C" stroke="#C9A84C" />}
                          </a>
                        </li>
                      ))}
                  </ul>
                  <div className="text-[10px] uppercase tracking-[0.24em] font-semibold text-slate-500 mb-2">
                    Síntesis estratégica
                  </div>
                  <a
                    href="#block-7"
                    className="flex items-center gap-3 rounded-xl px-3 py-2.5 transition-colors hover:brightness-105"
                    style={{
                      background: "linear-gradient(135deg, rgba(201,168,76,0.10) 0%, rgba(229,200,116,0.05) 100%)",
                      border: "1px solid rgba(201,168,76,0.35)",
                    }}
                    data-testid="consulting-nav-7"
                  >
                    <span
                      className="w-7 h-7 rounded-full text-[11px] font-bold flex items-center justify-center flex-shrink-0"
                      style={{
                        background: "linear-gradient(135deg, #C9A84C 0%, #E5C874 100%)",
                        color: "#0b3d2e",
                        boxShadow: "0 2px 6px -1px rgba(201,168,76,0.5)",
                      }}
                    >
                      7
                    </span>
                    <span className="text-sm font-semibold truncate" style={{ color: "#0b3d2e" }}>
                      {BLOCK_LABELS["7"]}
                    </span>
                    {priorities[7] && <Star className="w-3.5 h-3.5 flex-shrink-0" fill="#C9A84C" stroke="#C9A84C" />}
                  </a>
                </div>

                {/* Los 7 bloques */}
                {[1, 2, 3, 4, 5, 6, 7].map((n) => {
                  const isPriority = !!priorities[n];
                  const isEditable = EDITABLE_BLOCKS.has(n);
                  const isEditing = editingBlock === n;
                  const editedMeta = report.blocks_edited?.[String(n)];
                  const isEdited = !!editedMeta;
                  return (
                    <div
                      key={n}
                      id={`block-${n}`}
                      className="rounded-3xl p-6 md:p-8 mb-5 md:mb-6 consulting-block scroll-mt-8"
                      style={{
                        background: "#ffffff",
                        boxShadow: isPriority
                          ? "0 10px 30px -18px rgba(11,61,46,0.22), 0 0 0 1.5px rgba(201,168,76,0.55)"
                          : isEditing
                          ? "0 10px 30px -18px rgba(11,61,46,0.22), 0 0 0 2px rgba(15,110,86,0.55)"
                          : "0 10px 30px -18px rgba(11,61,46,0.18), 0 0 0 1px rgba(11,61,46,0.06)",
                      }}
                      data-testid={`consulting-report-block-${n}`}
                    >
                      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div
                            className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] font-semibold px-3 py-1.5 rounded-full"
                            style={{
                              background: "linear-gradient(135deg, #0b3d2e 0%, #0F6E56 100%)",
                              color: "#E5C874",
                              boxShadow: "0 4px 12px -4px rgba(11,61,46,0.35)",
                            }}
                          >
                            <span style={{ width: 5, height: 5, borderRadius: 999, background: "#C9A84C", display: "inline-block" }} />
                            Bloque {n} de 7 · {BLOCK_LABELS[n]}
                          </div>
                          {isEdited && !isEditing && (
                            <span
                              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold px-2 py-1 rounded-full"
                              style={{ background: "rgba(201,168,76,0.15)", color: "#8a6d2a", border: "1px solid rgba(201,168,76,0.35)" }}
                              title={`Editado ${editedMeta.edit_count} vez${editedMeta.edit_count > 1 ? "es" : ""} · última: ${new Date(editedMeta.last_edited_at).toLocaleDateString("es-ES")}`}
                              data-testid={`consulting-report-block-${n}-edited-badge`}
                            >
                              <Pencil className="w-3 h-3" strokeWidth={2} /> Editado por ti
                            </span>
                          )}
                        </div>
                        {/* Acciones por bloque */}
                        <div className="flex items-center gap-1">
                          {isEditing ? (
                            <>
                              {isEdited && (
                                <button
                                  onClick={() => restoreBlock(n)}
                                  disabled={editingSaving || restoringBlock === n}
                                  className="rounded-full px-3 py-1.5 text-xs font-semibold text-slate-500 hover:text-[#8a6d2a] hover:bg-[#C9A84C]/10 transition-colors inline-flex items-center gap-1 disabled:opacity-50"
                                  title="Descartar mis ediciones y volver al texto original generado por Claude"
                                  data-testid={`consulting-report-block-${n}-edit-restore`}
                                >
                                  {restoringBlock === n ? (
                                    <>
                                      <Loader2 className="w-3.5 h-3.5 animate-spin" /> Restaurando…
                                    </>
                                  ) : (
                                    <>
                                      <Undo2 className="w-3.5 h-3.5" /> Restaurar original
                                    </>
                                  )}
                                </button>
                              )}
                              <button
                                onClick={cancelEditing}
                                disabled={editingSaving}
                                className="rounded-full px-3 py-1.5 text-xs font-semibold text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-colors inline-flex items-center gap-1 disabled:opacity-50"
                                data-testid={`consulting-report-block-${n}-edit-cancel`}
                              >
                                <X className="w-3.5 h-3.5" /> Cancelar
                              </button>
                              <button
                                onClick={saveEditing}
                                disabled={editingSaving}
                                className="rounded-full px-3 py-1.5 text-xs font-bold inline-flex items-center gap-1 transition-all hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0"
                                style={{
                                  background: "linear-gradient(135deg, #0b3d2e 0%, #0F6E56 100%)",
                                  color: "#E5C874",
                                  boxShadow: "0 6px 16px -6px rgba(11,61,46,0.5)",
                                }}
                                data-testid={`consulting-report-block-${n}-edit-save`}
                              >
                                {editingSaving ? (
                                  <>
                                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> Validando…
                                  </>
                                ) : (
                                  <>
                                    <Check className="w-3.5 h-3.5" /> Guardar
                                  </>
                                )}
                              </button>
                            </>
                          ) : (
                            <>
                              {isEdited && isEditable && (
                                <button
                                  onClick={() => restoreBlock(n)}
                                  disabled={restoringBlock === n}
                                  className="rounded-full p-2 text-slate-400 hover:text-[#8a6d2a] hover:bg-[#C9A84C]/10 transition-colors disabled:opacity-50"
                                  title="Restaurar al texto original generado por Claude (descarta tus ediciones)"
                                  aria-label={`Restaurar bloque ${n} al original`}
                                  data-testid={`consulting-report-block-${n}-restore`}
                                >
                                  {restoringBlock === n ? (
                                    <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.75} />
                                  ) : (
                                    <Undo2 className="w-4 h-4" strokeWidth={1.75} />
                                  )}
                                </button>
                              )}
                              <button
                                onClick={() => startEditing(n)}
                                disabled={!isEditable}
                                className={`rounded-full p-2 transition-colors ${
                                  isEditable
                                    ? "text-slate-400 hover:text-[#0F6E56] hover:bg-[#0F6E56]/8"
                                    : "text-slate-200 cursor-not-allowed"
                                }`}
                                title={
                                  isEditable
                                    ? "Editar este bloque"
                                    : "Este bloque no es editable"
                                }
                                aria-label={isEditable ? `Editar bloque ${n}` : `Bloque ${n} no editable`}
                                data-testid={`consulting-report-block-${n}-edit`}
                              >
                                <Pencil className="w-4 h-4" strokeWidth={1.75} />
                              </button>
                              <button
                                onClick={() => copyBlock(n)}
                                className="rounded-full p-2 text-slate-400 hover:text-[#0F6E56] hover:bg-[#0F6E56]/8 transition-colors"
                                title="Copiar este bloque"
                                aria-label={`Copiar bloque ${n}`}
                                data-testid={`consulting-report-block-${n}-copy`}
                              >
                                <Copy className="w-4 h-4" strokeWidth={1.75} />
                              </button>
                              <button
                                onClick={() => askAboutBlock(n)}
                                className="rounded-full p-2 text-slate-400 hover:text-[#0F6E56] hover:bg-[#0F6E56]/8 transition-colors"
                                title="Preguntar sobre este bloque"
                                aria-label={`Preguntar sobre bloque ${n}`}
                                data-testid={`consulting-report-block-${n}-ask`}
                              >
                                <MessageCircle className="w-4 h-4" strokeWidth={1.75} />
                              </button>
                              <button
                                onClick={() => togglePriority(n)}
                                className="rounded-full p-2 transition-colors"
                                style={{
                                  color: isPriority ? "#C9A84C" : "#94a3b8",
                                  background: isPriority ? "rgba(201,168,76,0.12)" : "transparent",
                                }}
                                title={isPriority ? "Quitar prioridad" : "Marcar como prioritario"}
                                aria-label={`Marcar bloque ${n} como prioritario`}
                                aria-pressed={isPriority}
                                data-testid={`consulting-report-block-${n}-priority`}
                              >
                                <Star className="w-4 h-4" strokeWidth={1.75} fill={isPriority ? "#C9A84C" : "none"} />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                      {isEditing ? (
                        <>
                          {/* Toolbar minimal */}
                          <div
                            className="flex items-center gap-1 flex-wrap mb-3 p-2 rounded-xl border"
                            style={{ borderColor: "rgba(11,61,46,0.15)", background: "#f8fafc" }}
                            data-testid={`consulting-report-block-${n}-toolbar`}
                          >
                            <ToolbarBtn onClick={() => applyFormat("bold")} title="Negrita (Ctrl+B)"><Bold className="w-3.5 h-3.5" /></ToolbarBtn>
                            <ToolbarBtn onClick={() => applyFormat("italic")} title="Cursiva (Ctrl+I)"><Italic className="w-3.5 h-3.5" /></ToolbarBtn>
                            <span className="w-px h-4 bg-slate-300 mx-1" aria-hidden />
                            <ToolbarBtn onClick={() => applyFormat("insertUnorderedList")} title="Lista con viñetas"><List className="w-3.5 h-3.5" /></ToolbarBtn>
                            <ToolbarBtn onClick={() => applyFormat("insertOrderedList")} title="Lista numerada"><ListOrdered className="w-3.5 h-3.5" /></ToolbarBtn>
                            <span className="w-px h-4 bg-slate-300 mx-1" aria-hidden />
                            <ToolbarBtn
                              onClick={() => {
                                const url = window.prompt("URL del enlace:", "https://");
                                if (url) applyFormat("createLink", url);
                              }}
                              title="Insertar enlace"
                            ><LinkIcon className="w-3.5 h-3.5" /></ToolbarBtn>
                            <span className="w-px h-4 bg-slate-300 mx-1" aria-hidden />
                            <ToolbarBtn onClick={() => applyFormat("undo")} title="Deshacer (Ctrl+Z)"><Undo2 className="w-3.5 h-3.5" /></ToolbarBtn>
                            <button
                              onClick={resetToOriginalDraft}
                              className="ml-auto text-[11px] font-semibold text-slate-500 hover:text-[#0F6E56] px-2 py-1 rounded-md hover:bg-slate-100 transition-colors"
                              title="Descartar cambios y volver al estado al abrir el editor"
                              data-testid={`consulting-report-block-${n}-edit-reset`}
                            >
                              Descartar cambios
                            </button>
                          </div>
                          <div
                            ref={editorRef}
                            contentEditable
                            suppressContentEditableWarning
                            className="prose prose-slate max-w-none consulting-md focus:outline-none"
                            style={{
                              minHeight: 200,
                              padding: "12px 4px",
                              borderRadius: 8,
                              caretColor: "#0F6E56",
                            }}
                            data-testid={`consulting-report-block-${n}-editor`}
                          />
                          <p className="mt-2 text-[11px] text-slate-500">
                            El PDF y las descargas reflejarán tu edición. Si regeneras este bloque desde el panel, se perderá el texto editado.
                          </p>
                        </>
                      ) : (
                        <>
                          <div
                            className="prose prose-slate max-w-none consulting-md"
                            dangerouslySetInnerHTML={{ __html: marked.parse(report.blocks?.[n] || "", { async: false }) }}
                          />
                          {/* iter95 · CTA a la herramienta recomendada de este bloque */}
                          <BlockToolCTA blockN={n} testId={testId} navigate={navigate} />
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Spacer para que el contenido no quede oculto tras la barra fija en móvil */}
            <div
              aria-hidden
              style={{ height: "calc(84px + env(safe-area-inset-bottom))" }}
            />

            {/* Sticky bottom nav bar (solo móvil/tablet) */}
            <nav
              className="fixed bottom-0 left-0 right-0 z-40"
              style={{
                background: "rgba(255,255,255,0.92)",
                backdropFilter: "blur(14px)",
                WebkitBackdropFilter: "blur(14px)",
                boxShadow:
                  "0 -10px 28px -10px rgba(11,61,46,0.22), 0 -1px 0 rgba(11,61,46,0.08)",
                paddingBottom: "env(safe-area-inset-bottom)",
              }}
              aria-label="Navegación rápida de bloques"
              data-testid="consulting-report-mobile-nav"
            >
              <span
                aria-hidden
                className="block h-[2px] w-full"
                style={{
                  background:
                    "linear-gradient(90deg, transparent 0%, rgba(201,168,76,0.55) 50%, transparent 100%)",
                }}
              />
              <ul className="flex items-stretch justify-between gap-0.5 lg:gap-2 px-1.5 lg:px-8 py-2 lg:py-2.5">
                {[1, 2, 3, 4, 5, 6, 7].map((n) => {
                  const isActive = activeBlock === n;
                  const isPriority = !!priorities[n];
                  const shortLabel = BLOCK_SHORT_LABELS[n];
                  return (
                    <li key={n} className="flex-1 min-w-0 flex justify-center">
                      <a
                        href={`#block-${n}`}
                        onClick={(e) => {
                          e.preventDefault();
                          const el = document.getElementById(`block-${n}`);
                          if (el) {
                            el.scrollIntoView({ behavior: "smooth", block: "start" });
                            setActiveBlock(n);
                          }
                        }}
                        className="group relative flex flex-col lg:flex-row items-center justify-start lg:justify-center gap-1 lg:gap-2.5 py-0.5 lg:py-1.5 lg:px-2 lg:rounded-full lg:hover:bg-emerald-50/70 w-full min-w-0 transition-colors"
                        aria-current={isActive ? "true" : undefined}
                        aria-label={`Ir al bloque ${n}: ${BLOCK_LABELS[n]}`}
                        title={BLOCK_LABELS[n]}
                        data-testid={`consulting-report-mobile-nav-${n}`}
                      >
                        <span
                          className="relative inline-flex items-center justify-center rounded-full transition-all flex-shrink-0"
                          style={{
                            width: isActive ? 34 : 30,
                            height: isActive ? 34 : 30,
                            background: isActive
                              ? "linear-gradient(135deg, #0b3d2e 0%, #0F6E56 100%)"
                              : "linear-gradient(135deg, #C9A84C 0%, #E5C874 100%)",
                            color: isActive ? "#E5C874" : "#0b3d2e",
                            boxShadow: isActive
                              ? "0 6px 14px -6px rgba(11,61,46,0.55)"
                              : "0 2px 5px -1px rgba(201,168,76,0.45)",
                            fontSize: 12,
                            fontWeight: 700,
                            lineHeight: 1,
                          }}
                        >
                          {n}
                          {isPriority && (
                            <span
                              aria-hidden
                              className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full"
                              style={{
                                background: "#C9A84C",
                                boxShadow: "0 0 0 1.5px #ffffff",
                              }}
                            />
                          )}
                        </span>
                        <span
                          className="block lg:hidden w-full text-center leading-none truncate transition-colors"
                          style={{
                            fontSize: "clamp(9px, 2.6vw, 10px)",
                            fontWeight: isActive ? 700 : 500,
                            color: isActive ? "#0b3d2e" : "#64748b",
                            letterSpacing: "-0.01em",
                          }}
                        >
                          {shortLabel}
                        </span>
                        <span
                          className="hidden lg:block min-w-0 truncate text-[13px] leading-tight transition-colors"
                          style={{ fontWeight: isActive ? 700 : 500, color: isActive ? "#0b3d2e" : "#475569" }}
                        >
                          {BLOCK_LABELS[n]}
                        </span>
                      </a>
                    </li>
                  );
                })}
              </ul>
            </nav>
          </>
        )}
      </div>

      {/* Modal Share */}
      {shareOpen && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-0 sm:p-4"
          onClick={closeShare}
          data-testid="consulting-report-share-modal"
        >
          <div
            className="w-full sm:max-w-lg bg-white rounded-t-3xl sm:rounded-3xl overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            style={{
              boxShadow: "0 30px 60px -20px rgba(11,61,46,0.45), 0 0 0 1px rgba(201,168,76,0.15)",
            }}
          >
            <div
              className="relative px-6 py-5 flex items-center justify-between"
              style={{
                background: "linear-gradient(135deg, #0b3d2e 0%, #0F6E56 100%)",
              }}
            >
              <span
                aria-hidden
                className="absolute bottom-0 left-0 right-0 h-0.5"
                style={{ background: "linear-gradient(90deg, #C9A84C 0%, #E5C874 50%, #C9A84C 100%)" }}
              />
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: "#E5C874" }}>
                  Compartir informe
                </div>
                <div className="text-white font-semibold text-sm mt-0.5 truncate">
                  {report?.company_name}
                </div>
              </div>
              <button
                onClick={closeShare}
                className="rounded-full p-1.5 text-white/70 hover:text-white hover:bg-white/10 transition"
                aria-label="Cerrar"
                data-testid="consulting-report-share-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              {/* Public link */}
              <div>
                <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500 flex items-center gap-1.5 mb-2">
                  <Link2 className="w-3 h-3" /> Link público (solo-lectura)
                </label>
                {shareLoadingUrl ? (
                  <div className="flex items-center gap-2 text-slate-500 text-sm py-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> Generando link…
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      readOnly
                      value={shareUrl}
                      className="flex-1 min-w-0 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs md:text-sm text-slate-700 font-mono"
                      onClick={(e) => e.target.select()}
                      data-testid="consulting-report-share-url"
                    />
                    <button
                      onClick={copyShareUrl}
                      className="rounded-xl px-3 py-2 text-xs font-semibold inline-flex items-center gap-1.5 transition"
                      style={{
                        background: "linear-gradient(135deg, #0b3d2e 0%, #0F6E56 100%)",
                        color: "#ffffff",
                      }}
                      data-testid="consulting-report-share-copy"
                    >
                      <Copy className="w-3.5 h-3.5" /> Copiar
                    </button>
                  </div>
                )}
                <p className="text-[11px] text-slate-400 mt-2">
                  Cualquiera con este link podrá ver el informe. No verá tus respuestas ni datos internos.
                </p>
              </div>

              <div className="h-px bg-slate-100" />

              {/* Enviar por email */}
              <div>
                <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500 flex items-center gap-1.5 mb-2">
                  <Mail className="w-3 h-3" /> O envíalo por email (máx 5)
                </label>
                <input
                  type="text"
                  placeholder="jefa@empresa.com, socio@empresa.com"
                  value={shareEmails}
                  onChange={(e) => setShareEmails(e.target.value)}
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 focus:outline-none focus:border-[#0F6E56] focus:ring-2 focus:ring-[#0F6E56]/15"
                  data-testid="consulting-report-share-emails"
                />
                <textarea
                  placeholder="Mensaje opcional para el equipo…"
                  value={shareMessage}
                  onChange={(e) => setShareMessage(e.target.value)}
                  rows={2}
                  className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 focus:outline-none focus:border-[#0F6E56] focus:ring-2 focus:ring-[#0F6E56]/15 resize-none"
                  data-testid="consulting-report-share-message"
                />
                <button
                  onClick={sendShareEmail}
                  disabled={shareSending || !shareUrl}
                  className="mt-3 w-full rounded-full py-2.5 text-sm font-semibold text-white inline-flex items-center justify-center gap-2 disabled:opacity-50 transition-all hover:-translate-y-0.5"
                  style={{
                    background: "linear-gradient(135deg, #0b3d2e 0%, #14523f 55%, #0F6E56 100%)",
                    boxShadow: "0 8px 22px -8px rgba(11,61,46,0.5)",
                  }}
                  data-testid="consulting-report-share-send"
                >
                  {shareSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {shareSending ? "Enviando…" : "Enviar por email"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


/**
 * MadurezPill · pill sutil en el hero del informe con el índice 0-100 y un
 * ? con tooltip explicativo. Diseñado para no robar protagonismo a la
 * narrativa: aparece como elemento secundario en el hero verde.
 */
function MadurezPill({ pct, label, map = {} }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null); // {top, left} en viewport
  const btnRef = useRef(null);
  const AREAS = [
    { code: "A", name: "Marca empleadora" },
    { code: "B", name: "Selección" },
    { code: "C", name: "Onboarding" },
    { code: "D", name: "Desempeño" },
    { code: "E", name: "Feedback" },
    { code: "F", name: "Desarrollo" },
  ];
  const LEVEL_LABEL = ["—", "Crítico", "En desarrollo", "Funcional", "Sólido"];

  // Recomputa la posición del tooltip cuando se abre / cambia el viewport
  useEffect(() => {
    if (!open) return;
    function place() {
      const el = btnRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const TOOLTIP_W = window.innerWidth < 640 ? 300 : 380;
      // Alineamos horizontalmente al botón, pero clamp para que no salga del viewport
      let left = r.left;
      const maxLeft = window.innerWidth - TOOLTIP_W - 12;
      if (left > maxLeft) left = maxLeft;
      if (left < 12) left = 12;
      setPos({ top: r.bottom + 8, left, width: TOOLTIP_W });
    }
    place();
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [open]);

  return (
    <div className="mt-4 inline-flex items-center gap-2" data-testid="consulting-report-madurez-pill">
      <div
        className="inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-xs md:text-sm font-semibold"
        style={{
          background: "rgba(201,168,76,0.14)",
          border: "1px solid rgba(201,168,76,0.38)",
          color: "#F1E0B3",
        }}
      >
        <Gauge className="w-3.5 h-3.5" style={{ color: "#E5C874" }} />
        <span className="uppercase tracking-[0.16em] text-[10px]" style={{ color: "rgba(255,255,255,0.65)" }}>
          Índice de madurez
        </span>
        <span className="text-white tabular-nums font-bold">
          {pct}<span className="text-white/50 font-normal">/100</span>
        </span>
        {label && label !== "—" && (
          <>
            <span className="text-white/25">·</span>
            <span style={{ color: "#E5C874" }}>{label}</span>
          </>
        )}
      </div>
      <button
        ref={btnRef}
        type="button"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center justify-center w-6 h-6 rounded-full transition-colors focus:outline-none focus:ring-2"
        style={{
          background: "rgba(255,255,255,0.08)",
          border: "1px solid rgba(255,255,255,0.18)",
          color: "rgba(255,255,255,0.75)",
        }}
        aria-label="Qué significa el índice de madurez"
        data-testid="consulting-report-madurez-help"
      >
        <HelpCircle className="w-3.5 h-3.5" />
      </button>

      {open && pos && createPortal(
        <div
          className="fixed z-[100] rounded-2xl p-4 text-left"
          style={{
            top: pos.top,
            left: pos.left,
            width: pos.width,
            background: "#0b3d2e",
            border: "1px solid rgba(201,168,76,0.35)",
            boxShadow: "0 24px 48px -18px rgba(0,0,0,0.6)",
            color: "rgba(255,255,255,0.9)",
          }}
          onMouseEnter={() => setOpen(true)}
          onMouseLeave={() => setOpen(false)}
          data-testid="consulting-report-madurez-tooltip"
        >
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold mb-2" style={{ color: "#E5C874" }}>
            ¿Qué significa este número?
          </div>
          <p className="text-[13px] leading-relaxed mb-3 text-white/90">
            El <strong>índice de madurez</strong> resume en una escala de <strong>0 a 100</strong> el estado global de la
            gestión del talento de tu empresa. <strong>0</strong> equivale a todas las áreas en nivel <em>Crítico</em>;
            <strong> 100</strong>, a todas en nivel <em>Sólido</em>.
          </p>
          <div className="text-[11px] uppercase tracking-widest font-bold mb-2" style={{ color: "rgba(201,168,76,0.85)" }}>
            Cómo se calcula
          </div>
          <p className="text-[12.5px] leading-relaxed mb-3 text-white/85">
            Cada una de las 6 áreas evaluadas recibe un nivel cualitativo (Crítico&nbsp;=&nbsp;1, En desarrollo&nbsp;=&nbsp;2,
            Funcional&nbsp;=&nbsp;3, Sólido&nbsp;=&nbsp;4). Se calcula el promedio y se convierte a la escala 0-100 mediante
            <span className="font-mono text-[11.5px]"> (prom − 1) / 3 × 100</span>.
          </p>
          <div className="text-[11px] uppercase tracking-widest font-bold mb-2" style={{ color: "rgba(201,168,76,0.85)" }}>
            Rangos de la etiqueta
          </div>
          <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[12px] mb-3">
            <div className="flex items-center gap-2"><span className="font-mono text-white/70">0–24</span><span className="text-white font-semibold">Crítico</span></div>
            <div className="flex items-center gap-2"><span className="font-mono text-white/70">25–49</span><span className="text-white font-semibold">En desarrollo</span></div>
            <div className="flex items-center gap-2"><span className="font-mono text-white/70">50–74</span><span className="text-white font-semibold">Funcional</span></div>
            <div className="flex items-center gap-2"><span className="font-mono text-white/70">75–100</span><span className="text-white font-semibold">Sólido</span></div>
          </div>
          {Object.keys(map).length > 0 && (
            <>
              <div className="text-[11px] uppercase tracking-widest font-bold mb-2" style={{ color: "rgba(201,168,76,0.85)" }}>
                Niveles asignados
              </div>
              <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[12px]">
                {AREAS.map((a) => {
                  const n = Number(map[a.code] || 0);
                  return (
                    <div key={a.code} className="flex items-center gap-2 min-w-0">
                      <span
                        className="inline-flex items-center justify-center w-5 h-5 rounded font-bold text-[10px] flex-shrink-0"
                        style={{ background: "rgba(201,168,76,0.2)", color: "#E5C874" }}
                      >
                        {a.code}
                      </span>
                      <span className="truncate text-white/70">{a.name}:</span>
                      <span className="text-white font-semibold whitespace-nowrap">
                        {LEVEL_LABEL[n] || "—"}
                      </span>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}
